import React, { FC, useState, useEffect, useRef } from 'react';

type AutoHeightWrapperProps = {
  marginBottom?: number;
  children: React.ReactElement;
};

/**
 * A wrapper for DevExtreme DataGrid (or similar table components) that calculates the available vertical space
 * and applies it as the table’s max height. This ensures that:
 *  - With minimal data, the table fits its content.
 *  - With abundant data, the table occupies the available space and shows internal scrolling.
 *
 * The component measures its container’s top offset on mount and on window resize, then passes the calculated
 * maxHeight to its child via inline styles and a dynamic key to force re-rendering.
 *
 * @param props - Component props.
 * @param props.marginBottom - Optional bottom margin in pixels to subtract from the viewport height (default is 20).
 * @param props.children - The table component to be auto-sized (e.g. a DevExtreme DataGrid).
 */

export const AutoSizedTableWrapper: FC<AutoHeightWrapperProps> = ({
  marginBottom = 20,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setAvailableHeight(window.innerHeight - rect.top - marginBottom);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return (
    <div ref={containerRef}>
      {React.cloneElement(children, {
        key: availableHeight,
        height: 'auto',
        scrolling: { mode: 'standard', useNative: true },
        style: {
          ...children.props.style,
          maxHeight: `${availableHeight}px`,
        },
      })}
    </div>
  );
};
